// Story 8.11 (ADO-2020) — the pre-send review client:
//   GET /api/v1/basket/review?depotId=<uuid>
// The caller's active DRAFT cart grouped by catalogue department, each line with the
// date the Shopper last ordered it at this depot and a discontinued flag, plus the case
// totals. The wire shapes are a LOCK-STEP COPY of order-capture's exported
// `BasketReviewDto` / `BasketReviewGroupDto` / `BasketReviewLineDto` — keep them in sync.
// Each line carries the Money locked on its cart line (`unitPrice`, `lineTotal`) and, ADO-2246,
// the deposit locked with them (`depositPerUnit`, `lineDeposit`); a malformed deposit is
// dropped to absent (unknown) rather than failing the read. No margin is on the contract. The
// parse normalises the optional wire fields: an absent `lastOrderedAt`, `retailUnits` (ADO-2249),
// `packSize` or `unitOfMeasure` becomes `null` and an absent `lastOrderedUnavailable` becomes
// `false`, so the page never reads `undefined`.
// ADO-2263: each line's `imageUrl` passes through when it is a string; anything else
// (absent, `null`, malformed) becomes `null`, since a display-only image never fails the read.
// The money keys stay absent when the wire omits them (unknown money, never a zero).
// Pricing migration 18 (ADO-2249, DEC-6): each line also carries `priceKind` and `wasPrice`, locked
// with the cart line's unit price (row 12, ADO-2243; checked against its read by the story's Task
// 11.1: they match); an unknown kind or a malformed was price is dropped to absent, never a failed
// read. Pricing migration 17 (ADO-2248, AC14): each line also carries `rrp`, the RRP locked with
// the unit price, for the RSP column; a malformed one, or one not above zero, is dropped to absent,
// never a failed read. Story 11.10 (ADO-1961), D-3: each line also carries `margin`, the retail
// margin locked with the unit price, for the MARGIN column, on every line when the store sees its
// margin and on none when it does not; a malformed one is dropped to absent, never a failed read.
// Pricing migration 31 (ADO-1958), AC10: each line also carries the Mix & Match free units stored
// on its cart line and the deal that earned them; a malformed value is dropped to absent, never a
// failed read. The server's `caseCount` and `totalCases` already count them (AC5).

import { isMoneyValue, type MoneyValue } from '../shared/money';
import { cartTargetQuery, type CartTarget } from './cart-target';
import { isPriceKind, withFreeUnits } from './basket.client';
import { parseRetailMargin, type PriceKind, type RetailMarginDto } from './price-quotes.client';
import { API_PREFIX, apiFetch } from './shoplink-api';

export interface BasketReviewLine {
  readonly skuCode: string;
  readonly name: string;
  readonly quantity: number;
  /** UTC ISO 8601; `null` when this Shopper never ordered it at this depot, or when the
   *  review's `lastOrderedUnavailable` says the dates could not be read. */
  readonly lastOrderedAt: string | null;
  readonly discontinued: boolean;
  /** ADO-2249: the catalogue's retail units, verbatim; `null` when it holds none. PACK/CASE
   *  reads `{retailUnits} / {packSize}`. Optional, so a line built without it reads as none. */
  readonly retailUnits?: string | null;
  /** The catalogue's pack size label, verbatim; `null` when it holds none. */
  readonly packSize: string | null;
  /** The catalogue's unit of measure, verbatim; `null` when it holds none. */
  readonly unitOfMeasure: string | null;
  /** ADO-2263 — the catalogue image URL (already absolute); `null` when it holds none. */
  readonly imageUrl: string | null;
  /** The unit price locked on the cart line at add. The server
   *  always sends it; absent only when the wire carries none. */
  readonly unitPrice?: MoneyValue;
  /** The cart line's stored total, `unitPrice × quantity`. */
  readonly lineTotal?: MoneyValue;
  /** ADO-2246: the deposit per case locked on the cart line; absent means unknown (never
   *  `0.00`). Never part of `unitPrice` or `lineTotal`. */
  readonly depositPerUnit?: MoneyValue;
  /** ADO-2246: the line's deposit, `depositPerUnit × quantity`; absent means unknown. */
  readonly lineDeposit?: MoneyValue;
  /** Pricing migration 18 (ADO-2249, DEC-6): the cart line's price type and its per-case was
   *  price, locked with `unitPrice` (row 12, ADO-2243; checked against its read by Task 11.1).
   *  Absent when the wire carries none or a malformed value; absent never means zero. */
  readonly priceKind?: PriceKind;
  readonly wasPrice?: MoneyValue;
  /** Pricing migration 17 (ADO-2248, AC14): the RRP locked with `unitPrice`, for the RSP column.
   *  Absent when the line has none, or the wire carries a malformed or non-positive value. */
  readonly rrp?: MoneyValue;
  /** Story 11.10 (ADO-1961), D-3: the retail margin locked with `unitPrice`, for the MARGIN
   *  column. Absent from every line when the store does not see its margin. */
  readonly margin?: RetailMarginDto;
  /** Story 8.10 (ADO-2019): the part of the quantity that came from an HHT order, and the
   *  SKU this line is the accepted alternative for. Both absent on an older response. */
  readonly hhtQuantity?: number;
  readonly substitutedFromSku?: string | null;
  /** Story 18.18 (ADO-4037): the part of the quantity the RBP Shopping List added, for the
   *  "RBP NPR item" pill. Absent for a line added another way, on an older response, or when
   *  the wire carries a value that is not a number. */
  readonly rbpQuantity?: number;
  /** Pricing migration 31 (ADO-1958), AC10: the free units the cart line earned (charged
   *  nothing) and the deal that earned them. Absent on an older response or a malformed value. */
  readonly freeUnitsEarned?: number;
  readonly multibuyOfferCode?: string | null;
}

export interface BasketReviewGroup {
  /** `null` for lines with no department (the blank-heading group). */
  readonly departmentName: string | null;
  readonly caseCount: number;
  readonly lines: readonly BasketReviewLine[];
}

export interface BasketReview {
  readonly groups: readonly BasketReviewGroup[];
  readonly totalCases: number;
  /** The last-ordered lookup failed: every date is unknown, not "never ordered". */
  readonly lastOrderedUnavailable: boolean;
}

export const BASKET_REVIEW_PATH = `${API_PREFIX}/basket/review`;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** A line as the wire carries it: `lastOrderedAt` omitted when there is no date, and
 *  each pack fact omitted when the catalogue holds none. */
type WireReviewLine = Omit<
  BasketReviewLine,
  'lastOrderedAt' | 'retailUnits' | 'packSize' | 'unitOfMeasure' | 'imageUrl'
> & {
  readonly lastOrderedAt?: string;
  readonly retailUnits?: string;
  readonly packSize?: string;
  readonly unitOfMeasure?: string;
  /** Not validated: a non-string value is read as `null` (ADO-2263). */
  readonly imageUrl?: unknown;
  /** Not validated: a value that is not well-formed Money is read as absent (ADO-2246). */
  readonly depositPerUnit?: unknown;
  readonly lineDeposit?: unknown;
  /** Not validated: an unknown kind or malformed Money is read as absent (ADO-2249). */
  readonly priceKind?: unknown;
  readonly wasPrice?: unknown;
  /** Not validated: malformed Money, or an amount not above zero, is read as absent (ADO-2248). */
  readonly rrp?: unknown;
  /** Not validated: a malformed margin is read as absent (ADO-1961). */
  readonly margin?: unknown;
  /** Not validated: a malformed value is read as absent (ADO-1958). */
  readonly freeUnitsEarned?: unknown;
  readonly multibuyOfferCode?: unknown;
};

function isOptionalString(value: unknown): boolean {
  return value === undefined || typeof value === 'string';
}

function isOptionalMoney(value: unknown): boolean {
  return value === undefined || isMoneyValue(value);
}
type WireReviewGroup = Omit<BasketReviewGroup, 'lines'> & {
  readonly lines: readonly WireReviewLine[];
};

function isReviewLine(value: unknown): value is WireReviewLine {
  return (
    isRecord(value) &&
    typeof value['skuCode'] === 'string' &&
    typeof value['name'] === 'string' &&
    typeof value['quantity'] === 'number' &&
    typeof value['discontinued'] === 'boolean' &&
    isOptionalString(value['lastOrderedAt']) &&
    isOptionalString(value['retailUnits']) &&
    isOptionalString(value['packSize']) &&
    isOptionalString(value['unitOfMeasure']) &&
    isOptionalMoney(value['unitPrice']) &&
    isOptionalMoney(value['lineTotal']) &&
    (value['hhtQuantity'] === undefined || typeof value['hhtQuantity'] === 'number') &&
    (value['substitutedFromSku'] === undefined ||
      value['substitutedFromSku'] === null ||
      typeof value['substitutedFromSku'] === 'string')
  );
}

function isReviewGroup(value: unknown): value is WireReviewGroup {
  return (
    isRecord(value) &&
    (value['departmentName'] === null || typeof value['departmentName'] === 'string') &&
    typeof value['caseCount'] === 'number' &&
    Array.isArray(value['lines']) &&
    value['lines'].every(isReviewLine)
  );
}

/** Only the two Money fields, whatever else the wire object carries. */
function moneyCopy(money: MoneyValue): MoneyValue {
  return { amount: money.amount, currency: money.currency };
}

/** Validate the 2xx body; a contract drift fails loudly here rather than as a TypeError
 *  deep in the page render (the `basket.client.ts` parse posture). */
function parseReview(body: unknown): BasketReview {
  if (
    !isRecord(body) ||
    !Array.isArray(body['groups']) ||
    !body['groups'].every(isReviewGroup) ||
    typeof body['totalCases'] !== 'number' ||
    (body['lastOrderedUnavailable'] !== undefined && body['lastOrderedUnavailable'] !== true)
  ) {
    throw new Error('Basket review response was not in the expected shape');
  }
  return {
    groups: body['groups'].map((group) => ({
      departmentName: group.departmentName,
      caseCount: group.caseCount,
      lines: group.lines.map((line) => ({
        ...withFreeUnits({
          freeUnitsEarned: line.freeUnitsEarned,
          multibuyOfferCode: line.multibuyOfferCode,
        }),
        skuCode: line.skuCode,
        name: line.name,
        quantity: line.quantity,
        lastOrderedAt: line.lastOrderedAt ?? null,
        discontinued: line.discontinued,
        retailUnits: line.retailUnits ?? null,
        packSize: line.packSize ?? null,
        unitOfMeasure: line.unitOfMeasure ?? null,
        imageUrl: typeof line.imageUrl === 'string' ? line.imageUrl : null,
        ...(line.unitPrice !== undefined ? { unitPrice: moneyCopy(line.unitPrice) } : {}),
        ...(line.lineTotal !== undefined ? { lineTotal: moneyCopy(line.lineTotal) } : {}),
        ...(isMoneyValue(line.depositPerUnit)
          ? { depositPerUnit: moneyCopy(line.depositPerUnit) }
          : {}),
        ...(isMoneyValue(line.lineDeposit) ? { lineDeposit: moneyCopy(line.lineDeposit) } : {}),
        ...(isPriceKind(line.priceKind) ? { priceKind: line.priceKind } : {}),
        ...(isMoneyValue(line.wasPrice) ? { wasPrice: moneyCopy(line.wasPrice) } : {}),
        ...(isMoneyValue(line.rrp) && line.rrp.amount > 0 ? { rrp: moneyCopy(line.rrp) } : {}),
        ...marginOf(line.margin),
        ...(line.hhtQuantity !== undefined ? { hhtQuantity: line.hhtQuantity } : {}),
        ...(typeof line.rbpQuantity === 'number' ? { rbpQuantity: line.rbpQuantity } : {}),
        ...(line.substitutedFromSku !== undefined
          ? { substitutedFromSku: line.substitutedFromSku }
          : {}),
      })),
    })),
    totalCases: body['totalCases'],
    lastOrderedUnavailable: body['lastOrderedUnavailable'] === true,
  };
}

/** Fetch the review of `cart` (Story 8.3, ADO-2012, D-1: the depot plus the Shopper's
 *  Trading Account). No cart is `{ groups: [], totalCases: 0 }`; any non-2xx is a read
 *  failure the page offers to retry. */
export async function getBasketReview(cart: CartTarget): Promise<BasketReview> {
  const res = await apiFetch(`${BASKET_REVIEW_PATH}?${cartTargetQuery(cart)}`);
  if (!res.ok) throw new Error(`Failed to load the order review (${res.status})`);
  return parseReview(await res.json());
}

/** Story 11.10 (ADO-1961): a line's margin, or nothing when the wire carries none or a malformed
 *  one. */
function marginOf(value: unknown): { readonly margin?: RetailMarginDto } {
  const margin = parseRetailMargin(value);
  return margin === null ? {} : { margin };
}
